import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persist a snapshot of the just-completed purchase so the success page can
// render it after redirect. Used by both cart + single-package checkout flows.
function persistPurchaseSnapshot(order, response, verify) {
  try {
    sessionStorage.setItem(
      "sp-last-purchase",
      JSON.stringify({
        name: order.name,
        email: order.email,
        phone: order.phone || "",
        package_id: order.quote?.package_id || order.package_id || null,
        package_name: order.package_name || "Your Package",
        amount_paise: order.amount,
        subtotal_paise: order.subtotal_paise,
        gst_paise: order.gst_paise,
        order_id: response.razorpay_order_id,
        payment_id: response.razorpay_payment_id,
        coupon_code: order.coupon?.code || null,
        activation_token: verify?.activation_token || null,
        needs_password_setup: !!verify?.needs_password_setup,
        created_at: new Date().toISOString(),
      }),
    );
  } catch (_) {
    /* sessionStorage unavailable — success page will fall back to "no payment" view */
  }
}

// Empty the cart after a successful purchase so the next /cart visit is fresh.
function clearCartStorage() {
  try {
    localStorage.removeItem("sp-cart-v3");
    sessionStorage.removeItem("sp-pending-coupon");
  } catch (_) {
    /* noop */
  }
}

let scriptPromise;
function loadRazorpay() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  }
  return scriptPromise;
}

export async function startCartCheckout({ courses, name, email, phone, couponCode, consentAccepted, consentTimestamp }) {
  if (!Array.isArray(courses) || courses.length === 0) {
    toast.error("Please add at least one course to checkout.");
    return { ok: false };
  }
  if (!name?.trim() || !email?.trim()) {
    toast.error("Please share your name and email to continue.");
    return { ok: false };
  }
  if (!phone?.trim() || phone.trim().length < 6) {
    toast.error("Please share a valid phone number to continue.");
    return { ok: false };
  }
  if (!consentAccepted) {
    toast.error("Please accept the Terms & Conditions, Privacy Policy and Refund Policy to continue.");
    return { ok: false };
  }

  const ok = await loadRazorpay();
  if (!ok) {
    toast.error("Could not load checkout. Please retry.");
    return { ok: false };
  }

  let order;
  try {
    const { data } = await axios.post(`${API}/checkout/cart-order`, {
      courses,
      name,
      email,
      phone,
      coupon_code: couponCode || null,
      consent_accepted: !!consentAccepted,
      consent_timestamp: consentTimestamp || new Date().toISOString(),
    });
    order = data;
  } catch (err) {
    const detail =
      err?.response?.data?.detail || "Could not create order. Please try again.";
    toast.error(typeof detail === "string" ? detail : "Checkout failed.");
    return { ok: false };
  }

  if (!order.key_id) {
    toast.error(
      "Payments not configured yet. The site admin needs to add RAZORPAY_KEY_ID.",
    );
    return { ok: false };
  }

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "SkillPreneur Academy",
      description: order.package_name || `${(order.courses || []).length} courses`,
      prefill: {
        name: order.name,
        email: order.email,
        contact: order.phone || "",
      },
      theme: { color: "#0F172A" },
      handler: async (response) => {
        try {
          const { data: verify } = await axios.post(`${API}/checkout/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          persistPurchaseSnapshot(order, response, verify);
          clearCartStorage();
          resolve({ ok: true, response });
          // Hard redirect to the dedicated success page. Done here because the
          // Razorpay handler runs outside React Router's context.
          window.location.assign("/payment-success");
        } catch (e) {
          toast.error("Payment captured but verification failed. We'll reach out.");
          resolve({ ok: false });
        }
      },
      modal: {
        ondismiss: () => resolve({ ok: false, dismissed: true }),
      },
    });
    rzp.open();
  });
}

export async function startCheckout({ packageId, name, email, phone }) {
  if (!name?.trim() || !email?.trim()) {
    toast.error("Please share your name and email to continue.");
    return { ok: false };
  }

  const ok = await loadRazorpay();
  if (!ok) {
    toast.error("Could not load checkout. Please retry.");
    return { ok: false };
  }

  let order;
  try {
    const { data } = await axios.post(`${API}/checkout/order`, {
      package_id: packageId,
      name,
      email,
      phone,
    });
    order = data;
  } catch (err) {
    const detail =
      err?.response?.data?.detail || "Could not create order. Please try again.";
    toast.error(typeof detail === "string" ? detail : "Checkout failed.");
    return { ok: false };
  }

  if (!order.key_id) {
    toast.error(
      "Payments not configured yet. The site admin needs to add RAZORPAY_KEY_ID.",
    );
    return { ok: false };
  }

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "SkillPreneur Academy",
      description: order.package_name,
      prefill: {
        name: order.name,
        email: order.email,
        contact: order.phone || "",
      },
      theme: { color: "#0F172A" },
      handler: async (response) => {
        try {
          const { data: verify } = await axios.post(`${API}/checkout/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          persistPurchaseSnapshot({ ...order, package_id: packageId }, response, verify);
          clearCartStorage();
          resolve({ ok: true, response });
          window.location.assign("/payment-success");
        } catch (e) {
          toast.error("Payment captured but verification failed. We'll reach out.");
          resolve({ ok: false });
        }
      },
      modal: {
        ondismiss: () => resolve({ ok: false, dismissed: true }),
      },
    });
    rzp.open();
  });
}
